import type { Alert } from './api'
import type { AlertPoint } from './LiveMap'

// Cómo se ordena, se busca y se agrupa la cola de alertas. Son funciones puras: la pantalla solo
// las usa, y se prueban sin navegador.

export type SortMode = 'severity' | 'newest' | 'oldest' | 'unit'

export interface Bounds {
  west: number
  south: number
  east: number
  north: number
}

/** Un oficial que pide apoyo y nadie lo resolvió todavía: es su seguridad, no un desvío del plan. */
export const isOpenSupportRequest = (a: Alert) => a.kind === 'SupportRequested' && a.status !== 'Resolved'

/**
 * El orden de la cola.
 *
 * Por omisión manda la gravedad y, dentro de ella, lo más viejo primero: una alerta crítica que
 * lleva media hora sin atender es lo más urgente, y así no se queda atrás por ser antigua.
 *
 * Por encima de todo, y en cualquier orden, van los pedidos de apoyo abiertos. En la prueba
 * integral el pedido de un oficial quedó en el lugar 115 de 136, detrás de llegadas tardías de
 * hacía dos días: crítico como ellas y más nuevo (H-29).
 */
export function sortAlerts(alerts: Alert[], mode: SortMode): Alert[] {
  const support = alerts.filter(isOpenSupportRequest)
  // Después, lo escalado: alguien —el operador o el reloj— dijo que esto necesita al jefe de turno (H-66).
  const escalated = alerts.filter((a) => !isOpenSupportRequest(a) && a.status === 'Escalated')
  const rest = alerts.filter((a) => !isOpenSupportRequest(a) && a.status !== 'Escalated')
  // Entre pedidos de apoyo, el que más espera primero; lo mismo entre las escaladas.
  return [...sortBy(support, 'oldest'), ...sortBy(escalated, 'oldest'), ...sortBy(rest, mode)]
}

function sortBy(alerts: Alert[], mode: SortMode): Alert[] {
  const time = (a: Alert) => new Date(a.createdAt).getTime()
  const copy = alerts.slice()
  switch (mode) {
    case 'newest':
      return copy.sort((a, b) => time(b) - time(a))
    case 'oldest':
      return copy.sort((a, b) => time(a) - time(b))
    case 'unit':
      return copy.sort((a, b) => a.callSign.localeCompare(b.callSign) || time(a) - time(b))
    case 'severity':
    default:
      return copy.sort((a, b) => {
        const gravity = Number(b.severity === 'Critical') - Number(a.severity === 'Critical')
        return gravity !== 0 ? gravity : time(a) - time(b)
      })
  }
}

/**
 * Busca por unidad, tipo de desvío o lugar. Sin acentos y sin mayúsculas, porque nadie los escribe
 * bajo presión; varias palabras exigen que estén todas ("ae02 tarde").
 */
export function matchesQuery(alert: Alert, query: string): boolean {
  const words = normalize(query).split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const haystack = normalize([alert.callSign, alert.kind, alert.status, alert.message, Object.values(alert.args ?? {}).join(' ')].join(' '))
  return words.every((word) => haystack.includes(word))
}

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/** Lo que cae dentro de lo que se está mirando en el mapa. */
export function withinBounds(point: { latitude: number; longitude: number }, bounds: Bounds): boolean {
  return (
    point.latitude >= bounds.south &&
    point.latitude <= bounds.north &&
    point.longitude >= bounds.west &&
    point.longitude <= bounds.east
  )
}

/**
 * Las alertas que ocurrieron cerca de una dada, ella incluida.
 *
 * Varias alertas juntas en pocas cuadras rara vez son casualidad: suelen ser una unidad que no
 * salió, una zona que quedó sin cubrir o un problema de cobertura de datos. Verlas juntas es el
 * primer paso para tratarlas como un solo problema.
 */
export function nearbyAlertIds(points: AlertPoint[], alertId: string, meters: number): Set<string> {
  const center = points.find((p) => p.alertId === alertId)
  if (!center) return new Set([alertId])
  return new Set(points.filter((p) => distanceMeters(center, p) <= meters).map((p) => p.alertId))
}

/** Distancia en metros entre dos puntos (Haversine), la misma fórmula que usa SENTINEL. */
export function distanceMeters(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): number {
  const radius = 6_371_000
  const toRad = (degrees: number) => (degrees * Math.PI) / 180
  const dLat = toRad(b.latitude - a.latitude)
  const dLon = toRad(b.longitude - a.longitude)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2
  return 2 * radius * Math.asin(Math.sqrt(h))
}

/**
 * Agrupa las alertas que ocurrieron exactamente en el mismo punto.
 *
 * El plan revisita un hotspot varias veces en un turno, así que una unidad puede fallar dos visitas
 * al mismo sitio: son dos desvíos distintos, pero sus marcas caen una encima de otra y en el mapa
 * se ve una sola. Agrupándolas, el mapa dice cuántas hay y la cola deja mirarlas juntas.
 */
export function groupByPlace(points: AlertPoint[]): Map<string, AlertPoint[]> {
  const groups = new Map<string, AlertPoint[]>()
  for (const point of points) {
    const key = placeKey(point)
    const group = groups.get(key)
    if (group) group.push(point)
    else groups.set(key, [point])
  }
  return groups
}

/** Mismo punto = mismas coordenadas con precisión de unos pocos metros. */
export function placeKey(point: { latitude: number; longitude: number }): string {
  return `${point.latitude.toFixed(5)},${point.longitude.toFixed(5)}`
}

/** Las alertas que comparten punto con la dada, ella incluida. */
export function samePlaceAlertIds(points: AlertPoint[], alertId: string): string[] {
  const center = points.find((p) => p.alertId === alertId)
  if (!center) return [alertId]
  return points.filter((p) => placeKey(p) === placeKey(center)).map((p) => p.alertId)
}
